import { useMemo, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Add, Remove } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import {
  Box,
  BoxProps,
  Stack,
  Table as TableBase,
  TableBody as TableBodyBase,
  TableCell as TableCellBase,
  TableHead as TableHeadBase,
  TableRow,
  Collapse,
  Skeleton,
} from '@mui/material';
import type { StyledComponent } from 'styled-components';
import { ReactComponent as ArrowDownBase } from '../../assets/arrow-down.svg';
import { ReactComponent as ArrowUpBase } from '../../assets/arrow-up.svg';
import CheckBox from '../Checkbox';
import { Flex, FlexColumn } from '../Flex';
import OverflowTooltip from '../OverflowTooltip/OverflowTooltip';
import { getValue } from '../../utils/common';
import { componentToken } from '../../style/utils';
import type {
  Column,
  SortDirection,
  StickyColumnPosition,
  StickyColumnsLayout,
} from './types';

type RowProps<T> = {
  rowData: T;
  columns: Array<Column<T>>;
  onRowClick?: (rowData: T) => void;
  expandedRowRenderer?: (rowData: T) => JSX.Element | null;
  expandCondition?: (rowData: T) => boolean;
  expandOnLoad?: boolean;
  selectionColumn?: boolean | ((rowData: T) => boolean);
  selectionDisabled?: boolean;
  isSelected?: boolean;
  onSelectChange?: () => void;
  onLoaded?: () => void;
  animate?: boolean;
  isFocused?: boolean;
  centerRowCell?: boolean;
  columnTranslations?: Record<string, number>;
  isReordering?: boolean;
  isFirstInGroup?: boolean;
  compact?: boolean;
  stickyColumns?: StickyColumnsLayout;
};

type SortButtonsProps = {
  columnId: string;
  sortBy?: string;
  sortDirection: SortDirection;
  onSort: Function;
};

export const TABLE_HEAD_HEIGHT = 40;
export const MIN_COLUMN_WIDTH = 90;
const SELECTION_CELL_PADDING_LEFT = 16.5;
const SELECTION_CELL_PADDING_LEFT_ROW = 7;
const SELECTION_CELL_PADDING_RIGHT = 34;
export const SELECTION_COLUMN_WIDTH =
  SELECTION_CELL_PADDING_LEFT + SELECTION_CELL_PADDING_RIGHT;
export const EXPAND_COLUMN_WIDTH = 40;

export const TABLE_ROW_HEIGHT = 48;
export const TABLE_ROW_HEIGHT_COMPACT = 32;
export const TABLE_ROW_ESTIMATE = 60;
export const TABLE_ROW_ESTIMATE_COMPACT = 40;
export const GROUP_HEADER_HEIGHT = 56;
export const GROUP_HEADER_HEIGHT_COMPACT = 40;
const ROW_VERTICAL_PADDING = 14;
const ROW_VERTICAL_PADDING_COMPACT = 6;

export const Wrapper = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  overflow-y: hidden;
`;

type TableWrapperProps = BoxProps & { $bottomScrollPadding?: number };

export const TableWrapper: StyledComponent<
  typeof Box,
  any,
  TableWrapperProps,
  never
> = styled(Box)<TableWrapperProps>`
  height: 100%;
  overflow: auto;

  ::-webkit-scrollbar-track {
    margin-top: ${TABLE_HEAD_HEIGHT}px;
    margin-bottom: ${({ $bottomScrollPadding }) => $bottomScrollPadding || 0}px;
  }
`;

export const EmptyStateWrapper = styled(Flex)`
  height: calc(100% - ${TABLE_HEAD_HEIGHT}px);
  width: 100%;
`;

export const LoadingWrapper = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: center;
`;

export const StyledTable = styled(TableBase)`
  &.MuiTable-root {
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    width: 100%;
  }
`;

export const TableHead = styled(TableHeadBase)<{ $disabled?: boolean }>`
  background: ${componentToken('table.tableHead.background')};
  margin: 0;
  position: sticky;
  top: 0;
  z-index: 2;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

export const TableBody = TableBodyBase;

type StickyCellProps = {
  $sticky?: StickyColumnPosition;
  $customVariant?: string;
};

const STICKY_CELL_Z_INDEX = 1;
const DIVIDER_SHADOW_OPACITY = 0.08;
// Browsers can round a right-frozen cell a fraction of a pixel short of the
// scroll edge (e.g. next to an always-visible scrollbar), letting scrolled
// content show through. Overlapping the edge by 1px, which the scroll
// container clips, leaves no gap.
const RIGHT_EDGE_OVERLAP_PX = 1;

const stickyCellStyles = css<StickyCellProps>`
  ${({ $sticky, $customVariant, theme }) => {
    if (!$sticky) return '';

    const isHeaderCell = $customVariant === 'head';
    const isLeftSide = $sticky.side === 'left';
    const dividerLine = `inset ${isLeftSide ? '-1px' : '1px'} 0 0 ${
      theme.tokens.components.table.stickyCell.dividerColor
    }`;
    const dividerShadow = `${isLeftSide ? '' : '-'}4px 0 6px -6px ${alpha(
      theme.tokens.components.table.stickyCell.dividerShadowColor,
      DIVIDER_SHADOW_OPACITY,
    )}`;
    const offset = isLeftSide
      ? $sticky.offset
      : $sticky.offset - RIGHT_EDGE_OVERLAP_PX;

    return `
      position: sticky;
      ${$sticky.side}: ${offset}px;
      z-index: ${STICKY_CELL_Z_INDEX};
      background-color: ${
        isHeaderCell
          ? theme.tokens.components.table.stickyCell.background
          : 'inherit'
      };
      ${
        $sticky.hasDivider
          ? `box-shadow: ${dividerLine}, ${dividerShadow};`
          : ''
      }
    `;
  }}
`;

export const ColumnActions = styled(Flex)<{ $isActive?: boolean }>`
  display: ${({ $isActive }) => ($isActive ? 'flex' : 'none')};
  transition: opacity 0.15s ease;
  gap: ${componentToken('table.columnActions.gap')};
  flex-shrink: 0;

  ${({ $isActive }) =>
    !$isActive &&
    css`
      @starting-style {
        opacity: 0;
      }
    `}
`;

export const DraggableHeaderCell = styled(TableCellBase)<
  StickyCellProps & {
    $isDragging?: boolean;
    $isDraggable?: boolean;
    $translateX?: number;
    $isReordering?: boolean;
    $selectionColumn?: boolean;
  }
>`
  &.MuiTableCell-root {
    border: none !important;
    position: relative;
    padding-top: 0;
    padding-bottom: ${({ $selectionColumn }) =>
      $selectionColumn
        ? componentToken('table.headerCell.selectionPaddingBottom')
        : componentToken('table.headerCell.paddingBottom')};
    font-size: ${componentToken('table.headerCell.fontSize')};
    font-weight: ${componentToken('table.headerCell.fontWeight')};
    height: ${TABLE_HEAD_HEIGHT}px;
    white-space: nowrap;
    cursor: ${({ $isDraggable }) => ($isDraggable ? 'grab' : 'default')};
    user-select: none;
    transition: ${({ $isReordering }) =>
      $isReordering ? 'transform 0.2s ease, opacity 0.15s ease' : 'none'};
    transform: translateX(${({ $translateX }) => $translateX || 0}px);

    &:hover ${ColumnActions} {
      display: flex;
    }

    ${({ $isDragging }) =>
      $isDragging &&
      `
      opacity: 0.4;
      cursor: grabbing;
      z-index: 10;
    `}

    ${stickyCellStyles}
  }
`;

export const TableCell = styled(TableCellBase)<
  StickyCellProps & {
    $selectionColumn?: boolean;
    $centerRowCell?: boolean;
    $translateX?: number;
    $isReordering?: boolean;
    $hideTopBorder?: boolean;
    $compact?: boolean;
  }
>`
  &.MuiTableCell-root {
    border: none;
    border-top: ${({ theme, $hideTopBorder }) =>
      $hideTopBorder
        ? 'none'
        : `1px solid ${theme.tokens.components.table.tableCell.borderTop}`};
    transition: ${({ $isReordering }) =>
      $isReordering ? 'transform 0.2s ease' : 'none'};
    transform: translateX(${({ $translateX }) => $translateX || 0}px);
    vertical-align: ${({ $customVariant, $centerRowCell }) =>
      $customVariant === 'head' || $centerRowCell ? 'inherit' : 'top'};
    padding-right: ${({ $selectionColumn }) =>
      $selectionColumn
        ? `${SELECTION_CELL_PADDING_RIGHT}px`
        : componentToken('table.tableCell.paddingRight')};

    &:first-child {
      padding-left: ${({ $customVariant, $selectionColumn }) =>
        $selectionColumn
          ? $customVariant === 'head'
            ? `${SELECTION_CELL_PADDING_LEFT}px`
            : `${SELECTION_CELL_PADDING_LEFT_ROW}px`
          : 'auto'};
    }
    padding-top: ${({ $customVariant, $centerRowCell, $compact }) =>
      $customVariant === 'head' || $centerRowCell
        ? 0
        : $compact
        ? ROW_VERTICAL_PADDING_COMPACT
        : ROW_VERTICAL_PADDING}px;
    padding-bottom: ${({
      theme,
      $customVariant,
      $centerRowCell,
      $selectionColumn,
      $compact,
    }) =>
      $customVariant === 'head' && $selectionColumn
        ? `${theme.tokens.components.table.tableCell.headSelectionPaddingBottom} !important`
        : $customVariant === 'head' || $centerRowCell
        ? '0px'
        : `${
            $compact ? ROW_VERTICAL_PADDING_COMPACT : ROW_VERTICAL_PADDING
          }px`};
    height: ${({ $customVariant, $compact }) =>
      $customVariant === 'head'
        ? TABLE_HEAD_HEIGHT
        : $compact
        ? TABLE_ROW_HEIGHT_COMPACT
        : TABLE_ROW_HEIGHT}px;
    white-space: nowrap;
    font-size: ${componentToken('table.tableCell.fontSize')};

    ${({ $customVariant, $selectionColumn, theme }) =>
      $customVariant === 'head' &&
      `
        border: none !important;
        position: relative;
        padding-top: 0;
        padding-bottom: ${
          $selectionColumn
            ? theme.tokens.components.table.headerCell.selectionPaddingBottom
            : theme.tokens.components.table.headerCell.paddingBottom
        };
        font-size: ${theme.tokens.components.table.headerCell.fontSize};
        font-weight: ${theme.tokens.components.table.headerCell.fontWeight};
      `}

    ${({ $customVariant, theme }) =>
      $customVariant === 'expandedContent' &&
      `
        border: none !important;
        height: 0;
        padding: 0 ${theme.tokens.components.table.tableCell.expandedContentPadding};
        > .MuiCollapse-root {
          margin-bottom: ${theme.tokens.components.table.tableCell.expandedContentMarginBottom};
        }
      `}

    ${stickyCellStyles}
  }
`;

export const ArrowUp = styled(ArrowUpBase)<{ $active: boolean }>`
  cursor: pointer;
  path {
    fill: ${({ theme, $active }) =>
      $active
        ? theme.tokens.components.table.sortArrow.activeFill
        : theme.tokens.components.table.sortArrow.fill};
  }
  &:hover {
    path {
      fill: ${({ theme, $active }) =>
        $active
          ? theme.tokens.components.table.sortArrow.activeHoverFill
          : theme.tokens.components.table.sortArrow.hoverFill};
    }
  }
`;

export const ArrowDown = styled(ArrowDownBase)<{ $active: boolean }>`
  cursor: pointer;
  path {
    fill: ${({ theme, $active }) =>
      $active
        ? theme.tokens.components.table.sortArrow.activeFill
        : theme.tokens.components.table.sortArrow.fill};
  }
  &:hover {
    path {
      fill: ${({ theme, $active }) =>
        $active
          ? theme.tokens.components.table.sortArrow.activeHoverFill
          : theme.tokens.components.table.sortArrow.hoverFill};
    }
  }
`;

export const ClickableTableRow = styled(TableRow)<{
  $clickable?: boolean;
  $isSelected?: boolean;
  $animate?: boolean;
  $hasStickyColumns?: boolean;
}>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'auto')};
  background-color: ${({ theme, $isSelected, $hasStickyColumns }) =>
    $isSelected
      ? theme.tokens.components.table.clickableTableRow.selectedBackground
      : $hasStickyColumns &&
        theme.tokens.components.table.clickableTableRow.stickyBackground};
  isolation: ${({ $hasStickyColumns }) =>
    $hasStickyColumns ? 'isolate' : 'auto'};
  &:hover {
    background-color: ${({ theme, $clickable }) =>
      $clickable
        ? theme.tokens.components.table.clickableTableRow.hoverBackground
        : 'none'};
  }
  animation: ${({ $animate }) => $animate && 'blink 1000ms 4 ease-in-out'};

  @keyframes blink {
    0% {
      background-color: 'none';
    }
    50% {
      background-color: ${componentToken(
        'table.clickableTableRow.blinkBackground',
      )};
    }
    100% {
      background-color: 'none';
    }
  }
`;

export const ExpandButtonCell = styled(TableCell)`
  width: ${EXPAND_COLUMN_WIDTH}px;
  max-width: ${EXPAND_COLUMN_WIDTH}px;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  > svg {
    width: 18px;
    color: ${componentToken('table.expandButtonCell.svgColor')};
    margin-top: ${componentToken('table.expandButtonCell.svgMarginTop')};
    margin-left: -${componentToken('table.expandButtonCell.svgMarginLeft')};
  }
`;

export const SkeletonRow = styled(Flex)`
  height: 48px;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  border-top: ${({ theme }) =>
    `1px solid ${theme.tokens.components.table.skeletonRow.borderTop}`};
  gap: ${componentToken('table.skeletonRow.gap')};
  overflow: hidden;
`;

export const SkeletonCell = styled(Skeleton)`
  border-radius: ${componentToken('table.skeletonCell.borderRadius')};
  width: 163px;
  min-width: 163px;
  height: 20px;
`;

export const HeaderCellContent = styled(Flex)<{
  $centerContent?: boolean;
}>`
  flex-direction: row;
  gap: ${componentToken('table.headerCellContent.gap')};
  align-items: center;
  justify-content: ${({ $centerContent }) =>
    $centerContent ? 'center' : 'auto'};
  height: 100%;
`;

export const Resizer = styled(Flex)<{ $isResizing: boolean }>`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 15px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  z-index: 10;
  justify-content: center;

  &:after {
    content: '';
    height: 24px;
    width: 1px;
    background: ${componentToken('table.resizer.handleBackground')};
    transition: background 0.2s;
    border-radius: ${componentToken('table.resizer.handleRadius')};
    margin-top: ${componentToken('table.resizer.handleMarginTop')};
  }

  &:hover:after {
    background: ${componentToken('table.resizer.handleHoverBackground')};
    width: 2px;
  }

  ${({ $isResizing, theme }) =>
    $isResizing &&
    `
    &:after {
      background: ${theme.tokens.components.table.resizer.handleActiveBackground};
         width: 2px;
    }
  `}
`;

const isCellEmpty = <T,>(rowData: T, column: Column<T>) =>
  !column.metaColumn &&
  !column.showValueWhenEmpty &&
  !getValue(rowData, column.valueKey || column.id)
    ?.toString()
    .toLowerCase()
    .trim();

export const isRowSelectable = <T extends object>(
  selectionColumn: boolean | ((rowData: T) => boolean) | undefined,
  rowData: T,
) =>
  typeof selectionColumn === 'boolean'
    ? selectionColumn
    : selectionColumn?.(rowData);

export const Row = <T extends object>({
  rowData,
  columns,
  onRowClick,
  expandedRowRenderer,
  expandCondition,
  expandOnLoad,
  selectionColumn,
  selectionDisabled,
  isSelected,
  onSelectChange,
  onLoaded,
  animate,
  isFocused,
  centerRowCell,
  columnTranslations,
  isReordering,
  isFirstInGroup,
  compact,
  stickyColumns,
}: RowProps<T>) => {
  const [isExpanded, setIsExpanded] = useState(expandOnLoad);

  const isExpandEnabled = useMemo(
    () =>
      !!expandedRowRenderer && (!expandCondition || expandCondition?.(rowData)),
    [expandCondition, expandedRowRenderer, rowData],
  );

  const hasSelectionColumn = isRowSelectable(selectionColumn, rowData);
  const isSelectable = hasSelectionColumn && !selectionDisabled;

  useEffect(() => {
    onLoaded?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ClickableTableRow
        role="row"
        aria-label="table-row"
        $clickable={isExpandEnabled || isSelectable || !!onRowClick}
        onClick={() => {
          if (onRowClick) {
            onRowClick(rowData);
          } else if (isExpandEnabled) {
            setIsExpanded(!isExpanded);
          } else if (isSelectable) {
            onSelectChange?.();
          }
        }}
        $isSelected={isSelected || isFocused}
        $animate={animate}
        $hasStickyColumns={!!stickyColumns}
      >
        {selectionColumn && (
          <TableCell
            $customVariant={isExpanded ? 'expandedLeft' : ''}
            $sticky={stickyColumns?.selectionColumn}
            $selectionColumn={hasSelectionColumn}
            $hideTopBorder={isFirstInGroup}
            $compact={compact}
            onClick={(e) => {
              e.stopPropagation();
              if (isSelectable) onSelectChange?.();
            }}
          >
            <CheckBox
              sx={{
                ml: '9px',
                visibility: hasSelectionColumn ? 'visible' : 'hidden',
              }}
              $noPadding
              checked={!!isSelected}
              disabled={selectionDisabled}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onSelectChange?.()}
            />
          </TableCell>
        )}

        {columns.map((c, i) => (
          <TableCell
            role="cell"
            aria-label={c.header}
            key={c.id}
            align={
              c.centerContent ? 'center' : c.metaColumn ? 'right' : undefined
            }
            $centerRowCell={centerRowCell}
            $translateX={columnTranslations?.[c.id] || 0}
            $isReordering={isReordering}
            $hideTopBorder={isFirstInGroup}
            $compact={compact}
            $sticky={stickyColumns?.positions[c.id]}
            $customVariant={
              i === 0 && !selectionColumn && isExpanded ? 'expandedLeft' : ''
            }
          >
            {isCellEmpty(rowData, c) ? (
              '-'
            ) : c.renderer ? (
              c.renderer(rowData)
            ) : (
              <OverflowTooltip>
                {getValue(rowData, c.valueKey || c.id)?.toString()}
              </OverflowTooltip>
            )}
          </TableCell>
        ))}
        {!!expandedRowRenderer && (
          <ExpandButtonCell
            $customVariant={isExpanded ? 'expandedRight' : ''}
            $hideTopBorder={isFirstInGroup}
            $compact={compact}
            $sticky={stickyColumns?.expandColumn}
          >
            {isExpanded ? (
              <Remove
                sx={{
                  visibility: isExpandEnabled ? 'visible' : 'hidden',
                }}
              />
            ) : (
              <Add
                sx={{
                  visibility: isExpandEnabled ? 'visible' : 'hidden',
                }}
              />
            )}
          </ExpandButtonCell>
        )}
      </ClickableTableRow>
      <TableRow>
        <TableCell
          role="contentinfo"
          $customVariant="expandedContent"
          colSpan={
            expandedRowRenderer && selectionColumn
              ? columns.length + 2
              : expandedRowRenderer || selectionColumn
              ? columns.length + 1
              : columns.length
          }
        >
          <Collapse in={isExpanded} unmountOnExit sx={{ minWidth: '100%' }}>
            <Stack sx={{ p: 2 }}>{expandedRowRenderer?.(rowData)}</Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const SortButtons = ({
  columnId,
  sortBy,
  sortDirection,
  onSort,
}: SortButtonsProps) => (
  <Stack gap={0.6} justifyContent="center">
    <ArrowUp
      $active={sortBy === columnId && sortDirection === 'asc'}
      onClick={() => onSort(columnId, 'asc')}
    />
    <ArrowDown
      $active={sortBy === columnId && sortDirection === 'desc'}
      onClick={() => onSort(columnId, 'desc')}
    />
  </Stack>
);

export const TableLoadingState = ({ minWidth }: { minWidth: number }) => (
  <FlexColumn sx={{ minWidth: `${minWidth}px` }}>
    {Array.from({ length: 10 }).map((_, index) => (
      <SkeletonRow key={index}>
        {Array.from({ length: 20 }).map((_, cellIndex) => (
          <SkeletonCell key={cellIndex} variant="rectangular" />
        ))}
      </SkeletonRow>
    ))}
  </FlexColumn>
);
